import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined"
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import Typography from "@mui/material/Typography"
import * as fileDownload from "js-file-download"
import { useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import SegmentTracker from "../../../common/tracking"
import { useIsMobile } from "../../../common/utils"
import { useDialogContainer } from "../utils"
import DialogHeader from "./DialogHeader"

// Same schema as Upload Leads.
const CANDIDATE_CSV_COLUMNS = [
  "Name",
  "Phone",
  "Email",
  "Office Phone",
  "Company",
  "Address",
  "City",
  "State",
  "Zip",
  "License Type",
  "License Number",
  "Years in Business",
  "Sales Volume",
  "Homes Sold",
  "Rating",
  "Website",
  "LinkedIn",
  "Facebook",
  "Instagram",
  "Notes",
]

export default function ImportCandidatesDialog({
  open,
  onClose,
  onImportQueued,
}) {
  const [selectedFile, setSelectedFile] = useState(null)
  const [isImporting, setIsImporting] = useState(false)
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false)
  const [errorText, setErrorText] = useState(null)

  const isMobile = useIsMobile()
  const Container = useDialogContainer()
  const axios = useAuthorizedAxiosClient()

  function handleSoftClose() {
    if (!isImporting && !isDownloadingTemplate) {
      setErrorText(null)
      setSelectedFile(null)
      onClose()
    }
  }

  function handleFileChanged(e) {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0])
      setErrorText(null)
    } else {
      setSelectedFile(null)
    }
    // Also clear the file input value so selecting the same file again triggers onChange
    e.target.value = ""
  }

  function handleDownloadTemplateClicked() {
    setIsDownloadingTemplate(true)
    axios
      .get("/clients/api/candidates/export-csv-sample/", {
        responseType: "blob",
      })
      .then(({ data }) => fileDownload(data, "candidates-import-template.csv"))
      .catch((err) => {
        let errorMsg = "Failed to download template."
        if (err.response?.data?.detail) {
          errorMsg = err.response.data.detail
        } else if (err.response?.data?.error) {
          errorMsg = err.response.data.error
        }
        setErrorText(`Error: ${errorMsg}`)
      })
      .finally(() => setIsDownloadingTemplate(false))
  }

  function handleImportButtonClicked() {
    if (!selectedFile) return

    setIsImporting(true)
    setErrorText(null)

    const formData = new FormData()
    formData.append("file", selectedFile)

    axios
      .post("/clients/api/candidates/import-csv/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(({ data }) => {
        onImportQueued(data)
        SegmentTracker.track("Leads CSV import queued", {
          JobId: data.job_id,
        })
        setIsImporting(false)
        setErrorText(null)
        setSelectedFile(null)
        onClose()
      })
      .catch((err) => {
        let errorMsg = "An unexpected error occurred."
        const responseData = err.response?.data

        // Extracting error safely based on common formats
        if (responseData) {
          if (typeof responseData === "string") {
            errorMsg = responseData
          } else if (responseData.detail) {
            errorMsg = responseData.detail
          } else if (responseData.error) {
            errorMsg = responseData.error
          } else if (
            responseData.non_field_errors &&
            Array.isArray(responseData.non_field_errors) &&
            responseData.non_field_errors.length > 0
          ) {
            errorMsg = responseData.non_field_errors[0]
          } else if (
            Array.isArray(responseData) &&
            responseData.length > 0 &&
            typeof responseData[0] === "string"
          ) {
            errorMsg = responseData[0]
          } else if (
            typeof responseData === "object" &&
            Object.keys(responseData).length > 0
          ) {
            // Extract first available string error message from object properties
            const firstKey = Object.keys(responseData)[0]
            const firstVal = responseData[firstKey]
            if (
              Array.isArray(firstVal) &&
              firstVal.length > 0 &&
              typeof firstVal[0] === "string"
            ) {
              errorMsg = `${firstKey}: ${firstVal[0]}`
            } else if (typeof firstVal === "string") {
              errorMsg = `${firstKey}: ${firstVal}`
            } else {
              try {
                errorMsg = JSON.stringify(responseData)
              } catch (e) {}
            }
          }
        }
        setErrorText(`Error: ${errorMsg}`)
      })
      .finally(() => setIsImporting(false))
  }

  return (
    <Container
      open={open}
      onClose={handleSoftClose}
      anchor="right"
      fullScreen={isMobile}
    >
      <DialogHeader title="Import Leads" onClose={handleSoftClose} />
      <DialogContent>
        <Column sx={{ rowGap: 4, mt: 2 }}>
          <Alert severity="info">
            Upload a CSV with these columns: {CANDIDATE_CSV_COLUMNS.join(", ")}.
            Required columns: <strong>Name</strong>, <strong>Phone</strong>,{" "}
            <strong>Email</strong>. All other fields are optional. Imports run
            in the background. You&apos;ll get an in-app notification and email
            when your file has finished processing.
          </Alert>

          <Column sx={{ rowGap: 1 }}>
            <Typography variant="h6">Upload Candidate File</Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadOutlinedIcon />}
              sx={{ py: 2, borderStyle: "dashed", borderWidth: 2 }}
            >
              {selectedFile ? selectedFile.name : "Choose File"}
              <input
                type="file"
                hidden
                accept=".csv, .xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv"
                onChange={handleFileChanged}
              />
            </Button>
          </Column>

          <Row sx={{ justifyContent: "center", alignItems: "center" }}>
            <Typography variant="body2" color="textSecondary">
              OR
            </Typography>
          </Row>

          <Column sx={{ rowGap: 1 }}>
            <LoadingButton
              variant="outlined"
              color="primary"
              startIcon={<InsertDriveFileOutlinedIcon />}
              loading={isDownloadingTemplate}
              onClick={handleDownloadTemplateClicked}
            >
              Download Import Template
            </LoadingButton>
          </Column>

          {errorText && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorText}
            </Alert>
          )}
        </Column>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleSoftClose}
          disabled={isImporting || isDownloadingTemplate}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isImporting}
          disabled={!selectedFile || isDownloadingTemplate}
          onClick={handleImportButtonClicked}
        >
          Import
        </LoadingButton>
      </DialogActions>
    </Container>
  )
}
